import type {
  AdminChatMessageResponse,
  AdminChatRoomDetailResponse,
} from '@inflip/contracts/admin';
import {
  ChatMessageType,
  ChatParticipantRole,
  ChatRoomType,
  IP_RIGHT_LABELS,
  ORDER_STATUS_LABELS,
} from '@inflip/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AdminChatPanel,
  chatSendersOf,
  ROLE_LABEL,
  type ChatSenderInfo,
} from '../components/admin-chat-panel';
import { BackLink } from '../components/back-link';
import { Button, ErrorBanner, LoadingRow, PageHeader } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';

export function ChatRoomDetailPage() {
  const { session } = useAdminAuth();
  const { roomId } = useParams({ from: '/chats/$roomId' });
  const [detail, setDetail] = useState<AdminChatRoomDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .getChatRoom(session.accessToken, roomId)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, roomId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!session) return null;

  async function handleJoin() {
    if (!session || joining) return;
    setJoining(true);
    setError(null);
    try {
      await api.joinChatRoom(session.accessToken, roomId);
      // 참여 화면으로 바뀔 때까지 버튼을 막아 둔다
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    if (!session) return;
    await api.leaveChatRoom(session.accessToken, roomId);
    await load();
  }

  const senders = chatSendersOf(detail?.participants ?? []);

  return (
    <main className="w-full px-6 py-8">
      <div className="mb-2">
        <BackLink
          fallback="/chats"
          fallbackLabel="채팅 관리"
          className="text-sm font-medium text-brand hover:underline"
        />
      </div>
      <PageHeader title="채팅방 상세" />

      {error && <ErrorBanner message={error} />}
      {!detail && !error && <LoadingRow />}

      {detail && (
        <div className="flex flex-col gap-6 lg:flex-row">
          <section className="min-w-0 flex-1">
            {detail.viewerJoined ? (
              <AdminChatPanel
                token={session.accessToken}
                roomId={detail.id}
                senders={senders}
                onLeave={handleLeave}
              />
            ) : (
              <div className="space-y-3">
                <ReadOnlyMessages
                  token={session.accessToken}
                  roomId={detail.id}
                  senders={senders}
                />
                <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/60 p-4">
                  <span className="text-sm text-muted-foreground">
                    지금은 열람 모드예요. 참여하면 구매자·판매자와 직접 대화할 수 있어요.
                  </span>
                  <Button onClick={() => void handleJoin()} disabled={joining}>
                    {joining ? '참여 중…' : '대화 참여'}
                  </Button>
                </div>
              </div>
            )}
          </section>

          <aside className="w-full shrink-0 space-y-4 lg:w-72">
            <div className="rounded-lg bg-muted/60 p-4 text-sm">
              <h2 className="mb-3 text-xs font-bold text-muted-foreground">
                {detail.type === ChatRoomType.ORDER ? '거래 채팅' : '일반 1:1 채팅'}
              </h2>
              <div className="space-y-1.5">
                {detail.orders.length > 0 ? (
                  detail.orders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between gap-2">
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.id }}
                        className="min-w-0 truncate font-mono text-xs font-medium text-brand hover:underline"
                      >
                        {IP_RIGHT_LABELS[order.rightType]} · {order.id.slice(0, 8)}
                      </Link>
                      <span
                        className={`shrink-0 text-xs font-bold ${ORDER_STATUS_TEXT[order.status]}`}
                      >
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-muted-foreground">일반 1:1 (주문 없음)</div>
                )}
                <div className="text-xs text-muted-foreground">{detail.listingTitle ?? '—'}</div>
                <div className="text-xs text-muted-foreground">
                  방 생성 {formatDateTime(detail.createdAt)}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-muted/60 p-4 text-sm">
              <h2 className="mb-3 text-xs font-bold text-muted-foreground">참여자</h2>
              <ul className="space-y-2">
                {detail.participants.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {p.name ?? p.userId.slice(0, 8)}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {p.email ?? ''}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 text-xs font-bold ${
                        p.role === ChatParticipantRole.ADMIN
                          ? 'text-brand'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {ROLE_LABEL[p.role]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}

/** 참여 전 열람 전용 메시지 뷰 — api-admin REST 로 최신 50개. */
function ReadOnlyMessages({
  token,
  roomId,
  senders,
}: {
  token: string;
  roomId: string;
  senders: Record<string, ChatSenderInfo>;
}) {
  const [messages, setMessages] = useState<AdminChatMessageResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getChatRoomMessages(token, roomId)
      .then((rows) => !cancelled && setMessages(rows))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [token, roomId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="flex h-[60vh] flex-col rounded-lg border border-border bg-card shadow-soft">
      <header className="border-b border-border px-4 py-2.5 text-sm font-semibold">
        채팅 내역 (열람 전용)
      </header>
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
        {messages.map((m) =>
          m.type === ChatMessageType.SYSTEM ? (
            <div key={m.id} className="text-center text-xs text-muted-foreground">
              {m.content}
              {m.visibleToRole && (
                <span className="ml-1 font-semibold text-foreground/70">
                  ({ROLE_LABEL[m.visibleToRole]}에게만 보임)
                </span>
              )}
            </div>
          ) : (
            <div key={m.id} className="rounded-md bg-muted p-2.5">
              <div className="text-[10px] text-muted-foreground">
                {m.senderName ?? (m.senderId ? senders[m.senderId]?.name : null) ?? 'SYSTEM'}
                {m.senderRole ? ` (${ROLE_LABEL[m.senderRole]})` : ''} ·{' '}
                {formatDateTime(m.createdAt)}
              </div>
              <div>{m.content}</div>
            </div>
          ),
        )}
        {messages.length === 0 && (
          <div className="py-8 text-center text-xs text-muted-foreground">메시지가 없어요.</div>
        )}
      </div>
    </div>
  );
}
